import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LiveCard from "../common/LiveCard";
import { fetchLiveSessions } from "../../../redux/action/sessionAction";
import Shimmer from "../../common/shimmer/Shimmer";
import { Pagination } from "../../common/Pagination";
import useSocketSync from "../../hooks/useSocketSync";
import useTranslationHook from "../../hooks/useTranslationHook";
import SocketService from "../../../Service/SocketService";
import { DOMAIN } from "../../../utils/constants";
import ReactSelect from "../../common/ReactSelect";

const DATE_OPTIONS = [
    { label: "Any Date", value: 0 },
    { label: "Last 7 Days", value: 7 },
    { label: "Last 30 Days", value: 30 },
    { label: "Last 90 Days", value: 90 },
];

const TABS = [
    { label: "All non-ended", value: "non_ended" },
    { label: "Live", value: "live" },
    { label: "Upcoming", value: "upcoming" },
    { label: "Ended", value: "ended" },
];

const SessionList = ({ limit, showSeeAll, showFilters, showPagination, className = "" }) => {
    const dispatch = useDispatch();
    const { t } = useTranslationHook();
    const { state } = useLocation();
    const user = useSelector((state) => state.auth.user);
    const { data, page, page_size, totalPage, total, loading, type, dateFilter } =
        useSelector((state) => state.session.sessions);

    useEffect(() => {
        const requestedType = state?.defaultType ?? 'non_ended';
        if (!data.length || type !== requestedType || dateFilter !== 0 || page !== 1) {
            dispatch(fetchLiveSessions({ page: 1, type: requestedType, dateFilter: 0 }));
        }
        // eslint-disable-next-line
    }, []);

    const isConnected = useSelector((state) => state.socket.isConnected);

    const handleTabChange = (tabType) => {
        dispatch(fetchLiveSessions({ page: 1, type: tabType, dateFilter: 0 }));
    };

    const handleDateFilter = ({ value }) => {
        dispatch(fetchLiveSessions({ page: 1, dateFilter: Number(value) }));
    };

    const handlePageChange = (newPage) => {
        dispatch(fetchLiveSessions({ page: newPage }));
    };

    const listData = useMemo(
        () => (limit ? data.slice(0, limit) : data),
        [data, limit]
    );
    const listClass = listData.length === 1 ? "for1" : listData.length === 2 ? "for2" : "";
    const showingFrom = total === 0 ? 0 : (page - 1) * page_size + 1;
    const showingTo = Math.min(page * page_size, total);
    const syncData = useSocketSync(listData, user?.user_id ?? "", "session_sync_res");

    useEffect(() => {
        if (!isConnected) return;
        const handlePublicSync = (sessionIds) => {
            if (!Array.isArray(sessionIds) || !sessionIds.length) return;

            const incomingIds = new Set(sessionIds.map(Number));
            const existingIds = new Set(listData.map((s) => Number(s.id)));
            const matched = listData.filter((s) => incomingIds.has(Number(s.id)));
            const hasUnmatched = [...incomingIds].some((id) => !existingIds.has(id));

            if (matched.length) {
                const syncEmitData = matched.map((item) => ({
                    session_id: item.id,
                    domain_id: DOMAIN,
                    user_id: user?.user_id ?? "",
                }));
                SocketService.send("session_sync", {
                    data: syncEmitData,
                    emitterName: "session_sync_res",
                });
            }

            if (hasUnmatched && page === 1) {
                dispatch(fetchLiveSessions({ page: 1 }));
            }
        };

        SocketService.on("session_sync_public", handlePublicSync);
        return () => {
            SocketService.off("session_sync_public", handlePublicSync);
        };
    }, [isConnected, listData, user?.user_id, page, dispatch]);

    return (
        <section className={className}>
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="main-heading">
                            <h3>
                                <span>{t(showSeeAll ? "Live Auction" : "Live Sessions")}</span>
                            </h3>
                            {showSeeAll && (
                                <Link to="/live-auctions-listings" className="see-link">
                                    {t("See all")}{" "}
                                    <span className="arrow-wrap">
                                        <img
                                            className="arrow first"
                                            src="img/arrow-r.svg"
                                            alt="arrow right"
                                        />
                                        <img
                                            className="arrow second"
                                            src="img/arrow-r.svg"
                                            alt="arrow right"
                                        />
                                    </span>
                                </Link>
                            )}
                        </div>

                        {showFilters && (
                            <div className="session-middle">
                                <div className="session-left">
                                    <ul className="nav nav-pills" id="pills-tab" role="tablist">
                                        {TABS.map((tab) => (
                                            <li key={tab.value} className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link${type === tab.value ? " active" : ""}`}
                                                    onClick={() => handleTabChange(tab.value)}
                                                >
                                                    {t(tab.label)}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="total-record">
                                        {!loading && `Showing ${showingFrom}-${showingTo} out of ${total} Sessions`}
                                    </div>
                                </div>
                                <div className="session-right">
                                    <ReactSelect
                                        options={DATE_OPTIONS}
                                        value={DATE_OPTIONS.find((opt) => opt.value === dateFilter) ?? DATE_OPTIONS[0]}
                                        onChange={handleDateFilter}
                                        className="select"
                                        isSearchable={false}
                                    />
                                </div>
                            </div>
                        )}

                        {loading ? (
                            <ul className="auction-listt">
                                {[1, 2, 3].map((i) => (
                                    <li key={i}>
                                        <Shimmer type="rectangle" width="100%" height="300px" borderRadius="12px" />
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <>
                                <ul className={`auction-listt${listClass ? ` ${listClass}` : ""}`}>
                                    {listData.length ? (
                                        listData.map((ele, i) => {
                                            const sync = syncData?.find((s) => Number(s?.session_id) === Number(ele.id));
                                            const mergedEle = sync ? {
                                                ...ele,
                                                status: sync.session_status_id ?? ele.status,
                                                name: sync.session_name ?? ele.name,
                                                no_lot: sync.no_lots ?? ele.no_lot,
                                                minimum_deposit: sync.deposit_amount ?? ele.minimum_deposit,
                                                start_date: sync.session_start_date ?? ele.start_date,
                                                auctioneer_name: sync.auctioneer_name ?? ele.auctioneer_name,
                                                is_registered: sync.is_registered ?? ele.is_registered,
                                                is_payment_completed: sync.is_payment_completed != null
                                                    ? Boolean(Number(sync.is_payment_completed))
                                                    : ele.is_payment_completed,
                                                is_registration_approved: sync.is_registration_approved != null
                                                    ? Boolean(Number(sync.is_registration_approved))
                                                    : ele.is_registration_approved,
                                            } : ele;
                                            return (
                                                <LiveCard
                                                    key={ele.id ?? i}
                                                    ele={mergedEle}
                                                />
                                            );
                                        })
                                    ) : (
                                        <li className="full text-center">
                                            <img src="/img/no-property-found.png" alt={t("No live auction found")} />
                                            <h6>{t("No live auction found")}</h6>
                                        </li>
                                    )}
                                </ul>
                                {showPagination && totalPage > 1 && (
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPage}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SessionList;
